import React from 'react';
import styled from 'styled-components';
import { Insight } from '../../types/insight';

interface InsightCardProps {
  insight: Insight;
  onLike: (id: string) => void;
  onComment: (id: string) => void;
  onPin: (id: string) => void;
  onShare: (id: string) => void;
}

const Card = styled.div`
  background-color: #1C1628;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 280px;
  overflow: hidden;
  position: relative;
  border-radius: 12px 12px 0 0;
`;

const InsightImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ContentContainer = styled.div`
  padding: 20px;
`;

const Title = styled.h3`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SeeMore = styled.span`
  color: #9333EA;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-left: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  margin-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const BrandInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BrandLogo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background-color: #FF5722;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BrandName = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 500;
`;

const BrandSubtitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandCreator = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  font-weight: 400;
`;

const Actions = styled.div`
  display: flex;
  gap: 20px;
`;

const ActionButton = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: none;
  color: ${props => props.$active ? '#9333EA' : 'rgba(255, 255, 255, 0.7)'};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${props => props.$active ? '#9333EA' : '#fff'};
  }
  
  svg {
    width: 22px;
    height: 22px;
  }
`;

const InsightCard: React.FC<InsightCardProps> = ({
  insight,
  onLike,
  onComment,
  onPin,
  onShare
}) => {
  // Get first letter of brand name for fallback logo
  const brandInitial = insight.brand.name.charAt(0).toUpperCase();
  
  // Truncate description for display
  const truncateDescription = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };
  
  const displayDescription = truncateDescription(insight.description, 150);
  
  return (
    <Card>
      <ImageContainer>
        <InsightImage src={insight.imageUrl} alt={insight.title} />
      </ImageContainer>
      
      <ContentContainer>
        <Title>{insight.title}</Title>
        <Description>
          {displayDescription}
          {insight.description.length > 150 && (
            <SeeMore> see more</SeeMore>
          )}
        </Description>
        
        <Footer>
          <BrandInfo>
            <BrandLogo>
              {insight.brand.logo ? (
                <img src={insight.brand.logo} alt={insight.brand.name} />
              ) : (
                brandInitial
              )}
            </BrandLogo>
            <BrandSubtitle>
              <BrandName>{insight.brand.name}</BrandName>
              <BrandCreator>{insight.brand.creator || 'Kevin Smith'}</BrandCreator>
            </BrandSubtitle>
          </BrandInfo>
          
          <Actions>
            <ActionButton 
              $active={insight.isLiked}
              onClick={() => onLike(insight.id)}
              title="Like"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </ActionButton>
            
            <ActionButton 
              onClick={() => onComment(insight.id)}
              title="Comment"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z" />
              </svg>
            </ActionButton>
            
            <ActionButton 
              $active={insight.isPinned}
              onClick={() => onPin(insight.id)}
              title="Pin"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 4v7l2 3v2h-6v5l-1 1-1-1v-5H5v-2l2-3V4c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2zM9 4v7.75L7.5 14h9L15 11.75V4H9z" />
              </svg>
            </ActionButton>
            
            <ActionButton 
              onClick={() => onShare(insight.id)}
              title="Share"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92zM18 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM6 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 7.02c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
              </svg>
            </ActionButton>
          </Actions>
        </Footer>
      </ContentContainer>
    </Card>
  );
};

export default InsightCard;
